/*
 * List filter state <-> API query (overview section 2.1, "Quotes list").
 *
 * The UI holds ONE filter state per list; this file turns it into the contract's query
 * (`status[]=sent&status[]=viewed`, `needs_you=true`, `accepted_from`, `paid_in_full=false`, ...) and back.
 * Summary tiles are nothing but named filter states (see tiles.ts).
 */
import type {
  ClientFilter,
  InvoiceListQuery,
  InvoiceStatus,
  IsoDate,
  QuoteListQuery,
  QuoteStatus,
} from '@/types/quotes';

export interface QuoteFilterState {
  search: string;
  statuses: QuoteStatus[];
  needsYou: boolean;
  /** Set by the "Accepted" tile (the chosen period's dates). */
  acceptedFrom?: IsoDate;
  acceptedTo?: IsoDate;
  /** Set to false by the "Upcoming work" tile (quotes not yet paid in full). */
  paidInFull?: boolean;
  clientId?: string;
  /** User ids of the owners to show (admin and staff only). */
  owners?: number[];
}

export interface InvoiceFilterState {
  search: string;
  statuses: InvoiceStatus[];
  needsYou: boolean;
  /** Set by the "Paid" tile: invoices that became Paid within the chosen period. */
  paidFrom?: IsoDate;
  paidTo?: IsoDate;
  clientId?: string;
}

export interface ClientFilterState {
  search: string;
  filters: ClientFilter[];
}

export const EMPTY_QUOTE_FILTERS: QuoteFilterState = { search: '', statuses: [], needsYou: false };
export const EMPTY_INVOICE_FILTERS: InvoiceFilterState = { search: '', statuses: [], needsYou: false };
export const EMPTY_CLIENT_FILTERS: ClientFilterState = { search: '', filters: [] };

/** Number on the sliders button: every active filter counts once; the search text does not. */
export function quoteFilterCount(f: QuoteFilterState): number {
  let n = f.statuses.length;
  if (f.needsYou) n += 1;
  if (f.acceptedFrom || f.acceptedTo) n += 1;
  if (f.paidInFull === false) n += 1;
  n += f.owners?.length ?? 0;
  return n;
}

export function invoiceFilterCount(f: InvoiceFilterState): number {
  return f.statuses.length + (f.needsYou ? 1 : 0) + (f.paidFrom || f.paidTo ? 1 : 0);
}

export function clientFilterCount(f: ClientFilterState): number {
  return f.filters.length;
}

export function toggleInList<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

export function toQuoteQuery(f: QuoteFilterState, page = 1, perPage?: number): QuoteListQuery {
  const q: QuoteListQuery = { page };
  if (perPage) q.per_page = perPage;
  const search = f.search.trim();
  if (search) q.search = search;
  if (f.statuses.length) q.status = [...f.statuses];
  if (f.needsYou) q.needs_you = true;
  if (f.acceptedFrom) q.accepted_from = f.acceptedFrom;
  if (f.acceptedTo) q.accepted_to = f.acceptedTo;
  if (f.paidInFull === false) q.paid_in_full = false;
  if (f.clientId) q.client_id = f.clientId;
  if (f.owners?.length) q.owner_id = [...f.owners];
  return q;
}

export function toInvoiceQuery(f: InvoiceFilterState, page = 1, perPage?: number): InvoiceListQuery {
  const q: InvoiceListQuery = { page };
  if (perPage) q.per_page = perPage;
  const search = f.search.trim();
  if (search) q.search = search;
  if (f.statuses.length) q.status = [...f.statuses];
  if (f.needsYou) q.needs_you = true;
  if (f.paidFrom) q.paid_from = f.paidFrom;
  if (f.paidTo) q.paid_to = f.paidTo;
  if (f.clientId) q.client_id = f.clientId;
  return q;
}

export function toClientQuery(f: ClientFilterState, page = 1, perPage?: number) {
  const q: { page: number; per_page?: number; search?: string; filter?: ClientFilter[]; include?: 'properties' } = { page };
  if (perPage) q.per_page = perPage;
  const search = f.search.trim();
  if (search) q.search = search;
  if (f.filters.length) q.filter = [...f.filters];
  return q;
}

/**
 * The contract's multi-value convention: `name[]=a&name[]=b`, booleans as true/false, empty values dropped.
 * (Brackets stay literal so the URL reads the way the OpenAPI file documents it.)
 */
export function serializeQuery(params: object | undefined): string {
  if (!params) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const v of value) parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(String(v))}`);
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.join('&');
}

/** Short text for the "No quotes match ..." state. */
export function describeActiveFilters(f: QuoteFilterState | InvoiceFilterState): { search: string; statuses: string[] } {
  return { search: f.search.trim(), statuses: [...f.statuses] };
}
